
import { motion } from 'framer-motion';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import { useInView } from '../../../hooks/useInView';
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

interface CraneDetail {
  id: string;
  name: string;
  brand: string;
  capacity: string;
  boom: string;
  image: string;
  category: 'heavy' | 'light';
  description: string;
  features: string[];
  specs: {
    maxCapacity: string;
    boomLength: string;
    type: string;
    year: string;
    maxRadius: string;
    enginePower: string;
    travelSpeed: string;
    weight: string;
  };
  applications: string[];
  gallery: string[];
}

const CraneDetailPage = () => {
  const { category, id } = useParams<{ category: string; id: string }>();
  const [ref, isInView] = useInView({ threshold: 0.2 });
  const [crane, setCrane] = useState<CraneDetail | null>(null);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    // Heavy Cranes Data
    const heavyCranes: Record<string, CraneDetail> = {
      'sany-scc8500': {
        id: 'sany-scc8500',
        name: 'SANY SCC8500',
        brand: 'SANY',
        capacity: '500 Tons',
        boom: '108m',
        category: 'heavy',
        image: 'https://public.readdy.ai/ai/img_res/3519026c8db44cb558dc001ac75fe947.jpg',
        description: 'The SANY SCC8500 is a powerful crawler crane designed for the most demanding heavy lifting operations. With a maximum lifting capacity of 500 tons and a boom length of 108 meters, this crane is ideal for large-scale infrastructure projects, bridge construction, and industrial installations.',
        features: [
          'Maximum lifting capacity of 500 tons',
          'Extended boom reach up to 108 meters',
          'Advanced hydraulic system for smooth operation',
          'Intelligent control system with load monitoring',
          'Heavy-duty crawler tracks for stability',
          'Modular design for easy transportation',
          'Energy-efficient diesel engine',
          'Spacious operator cabin with climate control'
        ],
        specs: {
          maxCapacity: '500 tons',
          boomLength: '108m',
          type: 'Crawler Crane',
          year: '2022',
          maxRadius: '85m',
          enginePower: '450 HP',
          travelSpeed: '1.2 km/h',
          weight: '280 tons'
        },
        applications: [
          'Bridge Construction',
          'Power Plant Installation',
          'Petrochemical Projects',
          'Heavy Industrial Assembly',
          'Port and Harbor Construction',
          'Wind Turbine Installation'
        ],
        gallery: [
          'https://readdy.ai/api/search-image?query=massive%20SANY%20SCC8500%20crawler%20crane%20with%20500%20ton%20capacity%20on%20major%20construction%20site%20professional%20industrial%20photography%20heavy%20duty%20lifting%20equipment%20with%20extended%20lattice%20boom%20operator%20cabin%20visible%20sandy%20terrain%20Saudi%20Arabia%20bright%20daylight%20side%20angle%20showing%20full%20crane%20structure%20and%20immense%20power&width=1200&height=800&seq=sany-scc8500-gallery-1&orientation=landscape',
          'https://readdy.ai/api/search-image?query=SANY%20SCC8500%20crawler%20crane%20boom%20detail%20close%20up%20professional%20industrial%20photography%20lattice%20boom%20structure%20heavy%20duty%20lifting%20hook%20and%20cables%20visible%20construction%20site%20background%20Saudi%20Arabia%20bright%20daylight%20showing%20engineering%20precision%20and%20strength&width=1200&height=800&seq=sany-scc8500-gallery-2&orientation=landscape',
          'https://readdy.ai/api/search-image?query=SANY%20SCC8500%20crawler%20crane%20operator%20cabin%20interior%20professional%20industrial%20photography%20modern%20control%20panel%20digital%20displays%20comfortable%20operator%20seat%20safety%20features%20visible%20bright%20daylight%20showing%20advanced%20technology&width=1200&height=800&seq=sany-scc8500-gallery-3&orientation=landscape',
          'https://readdy.ai/api/search-image?query=SANY%20SCC8500%20crawler%20crane%20working%20on%20bridge%20construction%20professional%20industrial%20photography%20heavy%20lifting%20operation%20in%20progress%20massive%20concrete%20beam%20being%20lifted%20sandy%20terrain%20Saudi%20Arabia%20bright%20daylight%20showing%20crane%20capability%20and%20power&width=1200&height=800&seq=sany-scc8500-gallery-4&orientation=landscape'
        ]
      },
      'xcmg-xct300': {
        id: 'xcmg-xct300',
        name: 'XCMG XCT300',
        brand: 'XCMG',
        capacity: '300 Tons',
        boom: '88m',
        category: 'heavy',
        image: 'https://readdy.ai/api/search-image?query=powerful%20XCMG%20XCT300%20all%20terrain%20crane%20300%20ton%20capacity%20on%20construction%20site%20professional%20industrial%20photography%20modern%20mobile%20crane%20with%20telescopic%20boom%20multiple%20axles%20visible%20working%20on%20large%20project%20sandy%20terrain%20Saudi%20Arabia%20bright%20daylight%20three%20quarter%20angle%20showing%20crane%20versatility%20and%20strength&width=1200&height=800&seq=xcmg-xct300-detail-main&orientation=landscape',
        description: 'The XCMG XCT300 is a versatile all-terrain crane that combines exceptional lifting capacity with excellent mobility. With 300 tons of lifting power and an 88-meter boom, this crane excels in both urban and remote construction sites.',
        features: [
          'All-terrain capability for any job site',
          '300-ton maximum lifting capacity',
          'Telescopic boom extending to 88 meters',
          'Advanced safety systems and load monitoring',
          'Fast setup and deployment time',
          'Excellent maneuverability in tight spaces',
          'Fuel-efficient engine technology',
          'Comfortable operator cabin with full visibility'
        ],
        specs: {
          maxCapacity: '300 tons',
          boomLength: '88m',
          type: 'All Terrain Crane',
          year: '2023',
          maxRadius: '72m',
          enginePower: '380 HP',
          travelSpeed: '75 km/h',
          weight: '96 tons'
        },
        applications: [
          'High-Rise Construction',
          'Industrial Plant Installation',
          'Infrastructure Projects',
          'Precast Concrete Erection',
          'Steel Structure Assembly',
          'Equipment Installation'
        ],
        gallery: [
          'https://readdy.ai/api/search-image?query=powerful%20XCMG%20XCT300%20all%20terrain%20crane%20300%20ton%20capacity%20on%20construction%20site%20professional%20industrial%20photography%20modern%20mobile%20crane%20with%20telescopic%20boom%20multiple%20axles%20visible%20working%20on%20large%20project%20sandy%20terrain%20Saudi%20Arabia%20bright%20daylight%20three%20quarter%20angle%20showing%20crane%20versatility%20and%20strength&width=1200&height=800&seq=xcmg-xct300-gallery-1&orientation=landscape',
          'https://readdy.ai/api/search-image?query=XCMG%20XCT300%20crane%20telescopic%20boom%20extended%20professional%20industrial%20photography%20boom%20sections%20visible%20hydraulic%20cylinders%20construction%20site%20background%20Saudi%20Arabia%20bright%20daylight%20showing%20engineering%20design%20and%20reach%20capability&width=1200&height=800&seq=xcmg-xct300-gallery-2&orientation=landscape',
          'https://readdy.ai/api/search-image?query=XCMG%20XCT300%20crane%20multiple%20axles%20and%20outriggers%20professional%20industrial%20photography%20stabilization%20system%20deployed%20crane%20setup%20on%20construction%20site%20sandy%20terrain%20Saudi%20Arabia%20bright%20daylight%20showing%20stability%20and%20safety%20features&width=1200&height=800&seq=xcmg-xct300-gallery-3&orientation=landscape',
          'https://readdy.ai/api/search-image?query=XCMG%20XCT300%20crane%20lifting%20heavy%20steel%20beam%20professional%20industrial%20photography%20construction%20operation%20in%20progress%20workers%20visible%20on%20site%20sandy%20terrain%20Saudi%20Arabia%20bright%20daylight%20showing%20crane%20working%20capability&width=1200&height=800&seq=xcmg-xct300-gallery-4&orientation=landscape'
        ]
      }
    };

    // Light Cranes Data
    const lightCranes: Record<string, CraneDetail> = {
      'sany-stc250': {
        id: 'sany-stc250',
        name: 'SANY STC250',
        brand: 'SANY',
        capacity: '25 Tons',
        boom: '40m',
        category: 'light',
        image: 'https://readdy.ai/api/search-image?query=SANY%20STC250%20truck%20crane%2025%20ton%20capacity%20on%20construction%20site%20professional%20industrial%20photography%20compact%20mobile%20crane%20with%20telescopic%20boom%20working%20on%20residential%20building%20project%20sandy%20terrain%20Saudi%20Arabia%20bright%20daylight%20side%20angle%20showing%20crane%20agility%20and%20efficiency&width=1200&height=800&seq=sany-stc250-detail-main&orientation=landscape',
        description: 'The SANY STC250 is a compact and efficient truck crane perfect for urban construction projects. With 25 tons of lifting capacity and a 40-meter boom, this crane offers excellent maneuverability and quick setup times for residential and commercial applications.',
        features: [
          'Compact design for urban access',
          '25-ton lifting capacity',
          'Telescopic boom up to 40 meters',
          'Quick setup and deployment',
          'Excellent fuel efficiency',
          'Easy transportation between sites',
          'Advanced safety features',
          'User-friendly control system'
        ],
        specs: {
          maxCapacity: '25 tons',
          boomLength: '40m',
          type: 'Truck Crane',
          year: '2023',
          maxRadius: '34m',
          enginePower: '220 HP',
          travelSpeed: '80 km/h',
          weight: '32 tons'
        },
        applications: [
          'Residential Construction',
          'Commercial Buildings',
          'Roofing Projects',
          'HVAC Installation',
          'Sign Installation',
          'Tree Removal'
        ],
        gallery: [
          'https://readdy.ai/api/search-image?query=SANY%20STC250%20truck%20crane%2025%20ton%20capacity%20on%20construction%20site%20professional%20industrial%20photography%20compact%20mobile%20crane%20with%20telescopic%20boom%20working%20on%20residential%20building%20project%20sandy%20terrain%20Saudi%20Arabia%20bright%20daylight%20side%20angle%20showing%20crane%20agility%20and%20efficiency&width=1200&height=800&seq=sany-stc250-gallery-1&orientation=landscape',
          'https://readdy.ai/api/search-image?query=SANY%20STC250%20crane%20boom%20extended%20on%20residential%20site%20professional%20industrial%20photography%20telescopic%20boom%20sections%20visible%20lifting%20materials%20construction%20background%20Saudi%20Arabia%20bright%20daylight%20showing%20crane%20reach%20and%20versatility&width=1200&height=800&seq=sany-stc250-gallery-2&orientation=landscape',
          'https://readdy.ai/api/search-image?query=SANY%20STC250%20crane%20operator%20cabin%20and%20controls%20professional%20industrial%20photography%20modern%20control%20panel%20joysticks%20comfortable%20seat%20safety%20features%20visible%20bright%20daylight%20showing%20user%20friendly%20design&width=1200&height=800&seq=sany-stc250-gallery-3&orientation=landscape',
          'https://readdy.ai/api/search-image?query=SANY%20STC250%20crane%20working%20on%20commercial%20building%20professional%20industrial%20photography%20lifting%20construction%20materials%20workers%20on%20site%20sandy%20terrain%20Saudi%20Arabia%20bright%20daylight%20showing%20crane%20efficiency%20in%20urban%20environment&width=1200&height=800&seq=sany-stc250-gallery-4&orientation=landscape'
        ]
      },
      'xcmg-xct25': {
        id: 'xcmg-xct25',
        name: 'XCMG XCT25',
        brand: 'XCMG',
        capacity: '25 Tons',
        boom: '39.5m',
        category: 'light',
        image: 'https://readdy.ai/api/search-image?query=XCMG%20XCT25%20truck%20crane%2025%20ton%20capacity%20on%20construction%20site%20professional%20industrial%20photography%20modern%20compact%20mobile%20crane%20with%20telescopic%20boom%20working%20on%20commercial%20project%20sandy%20terrain%20Saudi%20Arabia%20bright%20daylight%20three%20quarter%20angle%20showing%20crane%20versatility%20and%20mobility&width=1200&height=800&seq=xcmg-xct25-detail-main&orientation=landscape',
        description: 'The XCMG XCT25 is a modern all-terrain truck crane designed for versatility and efficiency. With 25 tons of capacity and a 39.5-meter boom, this crane is perfect for a wide range of construction and installation projects.',
        features: [
          'All-terrain mobility',
          '25-ton maximum capacity',
          'Telescopic boom to 39.5 meters',
          'Fast travel speed between sites',
          'Efficient fuel consumption',
          'Advanced hydraulic system',
          'Compact footprint',
          'Easy operation and maintenance'
        ],
        specs: {
          maxCapacity: '25 tons',
          boomLength: '39.5m',
          type: 'Truck Crane',
          year: '2023',
          maxRadius: '33m',
          enginePower: '230 HP',
          travelSpeed: '85 km/h',
          weight: '34 tons'
        },
        applications: [
          'Building Construction',
          'Equipment Installation',
          'Material Handling',
          'Maintenance Work',
          'Utility Projects',
          'Landscaping'
        ],
        gallery: [
          'https://readdy.ai/api/search-image?query=XCMG%20XCT25%20truck%20crane%2025%20ton%20capacity%20on%20construction%20site%20professional%20industrial%20photography%20modern%20compact%20mobile%20crane%20with%20telescopic%20boom%20working%20on%20commercial%20project%20sandy%20terrain%20Saudi%20Arabia%20bright%20daylight%20three%20quarter%20angle%20showing%20crane%20versatility%20and%20mobility&width=1200&height=800&seq=xcmg-xct25-gallery-1&orientation=landscape',
          'https://readdy.ai/api/search-image?query=XCMG%20XCT25%20crane%20boom%20and%20hydraulic%20system%20professional%20industrial%20photography%20telescopic%20sections%20hydraulic%20cylinders%20visible%20construction%20site%20background%20Saudi%20Arabia%20bright%20daylight%20showing%20mechanical%20engineering%20and%20design&width=1200&height=800&seq=xcmg-xct25-gallery-2&orientation=landscape',
          'https://readdy.ai/api/search-image?query=XCMG%20XCT25%20crane%20outriggers%20deployed%20professional%20industrial%20photography%20stabilization%20system%20extended%20crane%20setup%20on%20site%20sandy%20terrain%20Saudi%20Arabia%20bright%20daylight%20showing%20safety%20and%20stability%20features&width=1200&height=800&seq=xcmg-xct25-gallery-3&orientation=landscape',
          'https://readdy.ai/api/search-image?query=XCMG%20XCT25%20crane%20lifting%20equipment%20on%20commercial%20site%20professional%20industrial%20photography%20construction%20operation%20materials%20being%20lifted%20workers%20visible%20sandy%20terrain%20Saudi%20Arabia%20bright%20daylight%20showing%20crane%20working%20in%20urban%20setting&width=1200&height=800&seq=xcmg-xct25-gallery-4&orientation=landscape'
        ]
      }
    };

    // Get crane data based on category and id
    const allCranes = category === 'heavy' ? heavyCranes : lightCranes;
    const craneData = allCranes[id || ''];
    
    if (craneData) {
      setCrane(craneData);
    }
  }, [category, id]);

  if (!crane) {
    return (
      <div className="min-h-screen bg-white font-inter flex items-center justify-center">
        <Navbar />
        <div className="text-center pt-32">
          <h1 className="text-4xl font-poppins font-black text-primary mb-4">Crane Not Found</h1>
          <Link to="/" className="text-accent hover:underline">Return to Home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-inter">
      <Navbar />
      
      <main className="pt-32">
        {/* Breadcrumb */}
        <section className="py-6 bg-neutral-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <Link to="/" className="hover:text-accent transition-colors duration-300">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/cranes/${category}`} className="hover:text-accent transition-colors duration-300 capitalize">{category} Cranes</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-primary font-semibold">{crane.name}</span>
            </div>
          </div>
        </section>

        {/* Hero Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Image Gallery */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="relative h-[500px] w-full rounded-3xl overflow-hidden mb-4">
                  <img
                    src={crane.gallery[selectedImage]}
                    alt={crane.name}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="grid grid-cols-4 gap-4">
                  {crane.gallery.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`relative h-24 w-full rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
                        selectedImage === index ? 'ring-4 ring-accent' : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${crane.name} ${index + 1}`}
                        className="w-full h-full object-cover object-center"
                      />
                    </button>
                  ))}
                </div>
              </motion.div>

              {/* Details */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <img 
                    src={crane.brand === 'SANY' ? 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8e/Sany_logo.svg/320px-Sany_logo.svg.png' : crane.brand === 'XCMG' ? 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f5/XCMG_logo.svg/320px-XCMG_logo.svg.png' : 'https://www.tadano.com/shared/images/common/logo.svg'} 
                    alt={crane.brand} 
                    className="h-8" 
                  />
                  <span className="px-4 py-1 bg-accent/10 text-accent rounded-full font-inter font-semibold text-sm capitalize">
                    {crane.category} Duty
                  </span>
                </div>

                <h1 className="text-5xl md:text-6xl font-poppins font-black text-primary mb-4">
                  {crane.name}
                </h1>

                <div className="flex items-center gap-6 mb-6">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                      <i className="ri-weight-line text-accent text-xl"></i>
                    </div>
                    <div>
                      <div className="text-2xl font-poppins font-bold text-primary">{crane.capacity}</div>
                      <div className="text-neutral-500 text-sm">Capacity</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                      <i className="ri-ruler-line text-accent text-xl"></i>
                    </div>
                    <div>
                      <div className="text-2xl font-poppins font-bold text-primary">{crane.boom}</div>
                      <div className="text-neutral-500 text-sm">Boom Length</div>
                    </div>
                  </div>
                </div>

                <p className="text-neutral-600 font-inter text-lg leading-relaxed mb-8">
                  {crane.description}
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <motion.a
                    href="/#contact"
                    onClick={(e) => {
                      e.preventDefault();
                      window.REACT_APP_NAVIGATE('/');
                      setTimeout(() => {
                        const element = document.querySelector('#contact');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }, 100);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 bg-accent text-white rounded-full font-poppins font-bold text-lg hover:shadow-xl transition-all duration-300 cursor-pointer text-center whitespace-nowrap"
                  >
                    Request Quote
                  </motion.a>
                  <motion.a
                    href="tel:+966123456789"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 bg-primary text-white rounded-full font-poppins font-bold text-lg hover:shadow-xl transition-all duration-300 cursor-pointer text-center whitespace-nowrap"
                  >
                    Call Now
                  </motion.a>
                </div>

                <div className="bg-neutral-50 rounded-2xl p-6">
                  <h3 className="text-xl font-poppins font-bold text-primary mb-4">Quick Specs</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-neutral-500 text-sm mb-1">Type</div>
                      <div className="font-inter font-semibold text-primary">{crane.specs.type}</div>
                    </div>
                    <div>
                      <div className="text-neutral-500 text-sm mb-1">Year</div>
                      <div className="font-inter font-semibold text-primary">{crane.specs.year}</div>
                    </div>
                    <div>
                      <div className="text-neutral-500 text-sm mb-1">Max Radius</div>
                      <div className="font-inter font-semibold text-primary">{crane.specs.maxRadius}</div>
                    </div>
                    <div>
                      <div className="text-neutral-500 text-sm mb-1">Engine Power</div>
                      <div className="font-inter font-semibold text-primary">{crane.specs.enginePower}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 bg-neutral-50" ref={ref}>
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-poppins font-black text-primary mb-4">
                Key Features
              </h2>
              <p className="text-neutral-500 font-inter text-lg">
                Advanced capabilities and specifications
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {crane.features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-accent text-xl"></i>
                  </div>
                  <p className="font-inter text-neutral-700 leading-relaxed">{feature}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Specifications Section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl md:text-5xl font-poppins font-black text-primary mb-8">
                  Technical Specifications
                </h2>
                <div className="space-y-4">
                  {Object.entries(crane.specs).map(([key, value], index) => (
                    <div key={index} className="flex items-center justify-between py-4 border-b border-neutral-100">
                      <span className="font-inter text-neutral-600 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <span className="font-poppins font-bold text-primary">{value}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl md:text-5xl font-poppins font-black text-primary mb-8">
                  Applications
                </h2>
                <div className="grid grid-cols-1 gap-4">
                  {crane.applications.map((app, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 bg-neutral-50 p-4 rounded-xl hover:bg-accent/5 transition-all duration-300"
                    >
                      <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-building-line text-white"></i>
                      </div>
                      <span className="font-inter text-neutral-700">{app}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-neutral-50">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative bg-gradient-to-br from-primary to-accent rounded-3xl p-12 md:p-16 overflow-hidden"
            >
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
              </div>

              <div className="relative z-10 text-center max-w-3xl mx-auto">
                <h2 className="text-4xl md:text-5xl font-poppins font-black text-white mb-6">
                  Ready to Rent This Crane?
                </h2>
                <p className="text-xl text-white/90 font-inter mb-8">
                  Contact us today for availability, pricing, and booking
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.a
                    href="/#contact"
                    onClick={(e) => {
                      e.preventDefault();
                      window.REACT_APP_NAVIGATE('/');
                      setTimeout(() => {
                        const element = document.querySelector('#contact');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }, 100);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 bg-white text-primary rounded-full font-poppins font-bold text-lg hover:shadow-xl transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    Get a Quote
                  </motion.a>
                  <motion.a
                    href="tel:+966123456789"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 bg-white/20 text-white rounded-full font-poppins font-bold text-lg hover:bg-white/30 backdrop-blur-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    Call Us Now
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CraneDetailPage;
